/**
 * Jasper Yeast — Sterling, Virginia.
 *
 * A liquid-yeast house whose range is unusually wide for its size: over a
 * hundred strains, organised by the style they are for rather than by a number
 * series, and reaching well past beer into sake, cider and seltzer. Each strain
 * has its own page carrying the analysis these entries are built from.
 *
 * Temperatures are the °C figures Jasper print beside their °F ones rather than
 * a conversion of our own. Where a culture publishes no full analysis — which is
 * usual for the bacteria and the Brettanomyces — the entry is `representative`
 * and its description says which figure is species-typical rather than theirs.
 */

import type { Yeast } from '../../schema/yeast';

export const jasperYeasts: readonly Yeast[] = [
  {
    id: 'jasper-jy001',
    lab: 'Jasper Yeast',
    productCode: 'JY001',
    name: 'Czech Lager I',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 69, max: 75 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Czech microbrewery isolate for smooth, lightly malty pilsners that still let hop aroma through. It is slow to start but finishes reliably, so Jasper suggests pitching warm on the first generation and lagering at least three weeks. Drops a very dense cake, which matters if you plan to repitch.',
    tags: ['lager', 'czech', 'pilsner', 'malty', 'slow-starter'],
    url: 'https://www.jasperyeast.com/yeasts/19-czech-lager-i',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy002',
    lab: 'Jasper Yeast',
    productCode: 'JY002',
    name: 'Düsseldorfer',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 15, max: 17 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A German ale strain built for Altbier and the cool-fermented German ale family. It finishes dry and crisp with restrained fruit esters over a smooth malt base, giving a lager-like impression from an ale fermentation. Moderate flocculation and a narrow cool temperature band mean it wants steady temperature control.',
    tags: ['altbier', 'german', 'dusseldorf', 'clean', 'crisp'],
    url: 'https://www.jasperyeast.com/yeasts/112-dusseldorfer',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy003',
    lab: 'Jasper Yeast',
    productCode: 'JY003',
    name: 'Bear Coast',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A West Coast production strain that ferments fast and throws very little ester character, so hops carry the beer. It drops brighter than a standard Chico-type culture, which makes it an easy swap when you want the same clean profile with less time in the tank before packaging.',
    tags: ['american', 'clean', 'ipa', 'pale-ale', 'west-coast'],
    url: 'https://www.jasperyeast.com/yeasts/1-bear-coast',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy005',
    lab: 'Jasper Yeast',
    productCode: 'JY005',
    name: 'Cologne Ale',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 13, max: 17 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A classic Kölsch strain that ferments cool and finishes dry, crisp and lightly acidic. Esters stay delicate, so the beer reads closer to a lager than an ale while keeping a soft ale mouthfeel. Suits Kölsch, Altbier and any sessionable ale where subtlety matters; it is a touch drier and more refreshing than JY158.',
    tags: ['kolsch', 'german', 'clean', 'crisp', 'sessionable'],
    url: 'https://www.jasperyeast.com/yeasts/79-cologne-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy008',
    lab: 'Jasper Yeast',
    productCode: 'JY008',
    name: 'Saint Nicholas',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 78 },
    temperatureC: { min: 20, max: 29 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A fruit-forward Poperinge house strain that covers most of the Belgian range, from blondes and pale ales up through dubbel, tripel and quad. Cool fermentation keeps the malt in front while the warm end pushes esters hard. Attenuation is only moderate, so a step mash helps it finish where you want it.',
    tags: ['belgian', 'abbey', 'estery', 'tripel', 'wide-temperature-range'],
    url: 'https://www.jasperyeast.com/yeasts/26-saint-nicholas',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy010',
    lab: 'Jasper Yeast',
    productCode: 'JY010',
    name: 'Spirit Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 90 },
    temperatureC: { min: 23, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: true,
    description:
      'An Ardennes farmhouse strain with a lot of personality, turning creamy citrus and orange notes when pushed to the top of its range. It wants real heat: pitching cool wastes it and invites a stall. STA1 positive and very attenuative, so expect a bone-dry beer and treat cross-contamination seriously.',
    tags: ['saison', 'farmhouse', 'diastatic', 'citrus', 'high-attenuation', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/35-spirit-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy011',
    lab: 'Jasper Yeast',
    productCode: 'JY011',
    name: 'Albert',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'An East Flanders top-cropping strain that leans malty and rounded rather than aggressive: spicy phenols, ripe fruit and a little residual sweetness. The modest attenuation and cool working range suit Belgian table beer, patersbier and other low to medium gravity beers built for drinkability.',
    tags: ['belgian', 'table-beer', 'phenolic', 'malty', 'session'],
    url: 'https://www.jasperyeast.com/yeasts/80-albert',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy014',
    lab: 'Jasper Yeast',
    productCode: 'JY014',
    name: 'The Pont',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 90 },
    temperatureC: { min: 25, max: 33 },
    flocculation: 'low',
    alcoholTolerancePercent: 7,
    sta1: true,
    description:
      'The classic Belgian saison strain, capable of finishing bone dry with a spicy, tart, sometimes bubblegum character. It is also the notorious staller: keep the gravity sensible, aerate well and let the temperature free-rise without cooling, or it will sit at half attenuation for weeks. STA1 positive.',
    tags: ['saison', 'farmhouse', 'diastatic', 'stalling-risk', 'dry-finish', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/33-the-pont',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy017',
    lab: 'Jasper Yeast',
    productCode: 'JY017',
    name: 'High and Dry',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 84 },
    temperatureC: { min: 18, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A neutral, quick Californian strain built for high-gravity hoppy beer, finishing dry without adding flavor of its own. Ferment it under low head pressure: spunding too early pushes sulfur up and strips hop aroma.',
    tags: ['american', 'clean', 'high-gravity', 'double-ipa', 'dry-finish'],
    url: 'https://www.jasperyeast.com/yeasts/3-high-and-dry',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy018',
    lab: 'Jasper Yeast',
    productCode: 'JY018',
    name: 'Cider',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 15, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A dry-finishing fermenter for apple cider, perry and cyser that also copes with strong beer and wine musts. It works across an unusually wide temperature band and tolerates alcohol well. Underfed ferments can throw sulfur early on, though it clears with a little time and proper nutrient additions.',
    tags: ['cider', 'perry', 'dry-finish', 'wide-temp', 'high-tolerance'],
    url: 'https://www.jasperyeast.com/yeasts/34-cider',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy025',
    lab: 'Jasper Yeast',
    productCode: 'JY025',
    name: 'North Woods',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Michigan brewery strain that works more slowly than most American ale yeasts but keeps a steady pace. Held in range it leaves a soft, faintly creamy yeast note, which suits American wheat beers as well as ambers, reds and stouts.',
    tags: ['american', 'wheat', 'amber', 'stout', 'soft'],
    url: 'https://www.jasperyeast.com/yeasts/4-north-woods',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy027',
    lab: 'Jasper Yeast',
    productCode: 'JY027',
    name: 'Scourmont',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 81 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Trappist strain from one of the six Belgian monastery breweries, swinging from spicy and restrained when kept cool to strongly fruity when run warm. It handles the whole abbey range and blends well with other Belgian strains such as JY240.',
    tags: ['belgian', 'trappist', 'abbey', 'estery', 'blending-strain'],
    url: 'https://www.jasperyeast.com/yeasts/27-scourmont',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy028',
    lab: 'Jasper Yeast',
    productCode: 'JY028',
    name: 'Whiteshield',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Burton-upon-Trent classic that leans mineral and dry, keeping the spotlight on malt and hops rather than on itself. Warmer ferments coax out light orchard and stone-fruit esters. It wants sulfate-rich brewing liquor; very soft water tends to leave it sluggish. Suits bitters, porters and barleywines alike.',
    tags: ['english', 'burton', 'bitter', 'porter', 'mineral', 'dry'],
    url: 'https://www.jasperyeast.com/yeasts/81-whiteshield',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy033',
    lab: 'Jasper Yeast',
    productCode: 'JY033',
    name: 'Brett brux Amsterdam',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 15, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Brettanomyces bruxellensis isolated in Amsterdam: slow-growing, clean but distinctly funky. Use it for Orval-style bottle conditioning over a primary Saccharomyces beer, or alongside Lactobacillus and Pediococcus in a mixed-culture sour. Jasper publishes attenuation only as 80+% and does not publish an alcohol tolerance, so the attenuation ceiling here is representative rather than measured.',
    tags: ['brettanomyces', 'wild', 'funky', 'bottle-conditioning', 'mixed-culture'],
    url: 'https://www.jasperyeast.com/yeasts/45-brett-brux-amsterdam',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy037',
    lab: 'Jasper Yeast',
    productCode: 'JY037',
    name: 'ESB',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 78 },
    temperatureC: { min: 16, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A hard-wearing all-rounder that stretches well beyond its namesake style. Expect a touch of fruit over a solid, dependable ferment that shrugs off rough handling. Equally at home in stouts, hazy IPAs, American pale ales and barleywines.',
    tags: ['english', 'esb', 'versatile', 'ipa', 'stout'],
    url: 'https://www.jasperyeast.com/yeasts/14-esb',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy038',
    lab: 'Jasper Yeast',
    productCode: 'JY038',
    name: 'O’Malley’s Irish',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 75 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The Dublin dry stout strain, and about as recognizable as Irish yeast gets. It finishes crisp and clean, though a faint buttery note can linger once primary is done, so give it a proper diacetyl rest. Warmer fermentations bring out a little more fruit.',
    tags: ['irish', 'dry-stout', 'dublin', 'clean', 'diacetyl-rest'],
    url: 'https://www.jasperyeast.com/yeasts/15-o-malley-s-irish',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy039',
    lab: 'Jasper Yeast',
    productCode: 'JY039',
    name: 'Founder',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'An East Coast craft strain that stays mostly out of the way, contributing only a light fruitiness. Moderate attenuation leaves body behind, so malt-forward ales and East Coast IPAs keep their weight rather than finishing thin.',
    tags: ['american', 'east-coast', 'ipa', 'malt-forward', 'balanced'],
    url: 'https://www.jasperyeast.com/yeasts/82-founder',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy056',
    lab: 'Jasper Yeast',
    productCode: 'JY056',
    name: 'Poperinge Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 79, max: 85 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: true,
    description:
      'A fast, dependable saison workhorse that reaches terminal gravity quickly and gives lime and lemon aromatics over light white pepper. Unusually for the style it does not need extreme heat, and its higher alcohol tolerance makes it a good rescue or co-pitch for finicky saisons that have stopped short. STA1 positive.',
    tags: ['saison', 'farmhouse', 'diastatic', 'citrus', 'high-attenuation', 'blending-strain'],
    url: 'https://www.jasperyeast.com/yeasts/28-poperinge-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy061',
    lab: 'Jasper Yeast',
    productCode: 'JY061',
    name: 'Brett Brux Chateaux',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 15, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A Brettanomyces bruxellensis pulled from a spontaneous Belgian culture, giving a rounded, classic Brett character rather than an aggressive one. It suits funky IPA, sour beer and especially wood- and barrel-aged Brett beers where time does the work. Jasper publishes attenuation only as 80+% and no alcohol tolerance, so the upper attenuation figure here is representative.',
    tags: ['brettanomyces', 'wild', 'belgian', 'barrel-aged', 'funky'],
    url: 'https://www.jasperyeast.com/yeasts/46-brett-brux-chateaux',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy064',
    lab: 'Jasper Yeast',
    productCode: 'JY064',
    name: 'Wevelgem',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 17, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A versatile Belgian strain that straddles saison and Belgian pale ale, giving peppery phenolics at the cool end and rounder fruit as it warms. It stays reliable across an unusually wide range and finishes crisp and dry, which suits hop-forward Belgian beers. Jasper describes it as diastatic, so it will keep chewing on dextrins; watch packaged beer and keep it away from clean house strains.',
    tags: ['belgian', 'saison', 'peppery', 'diastatic', 'hop-forward', 'wide-temperature-range'],
    url: 'https://www.jasperyeast.com/yeasts/94-wevelgem',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy087',
    lab: 'Jasper Yeast',
    productCode: 'JY087',
    name: 'Sacc Brux',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 21, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: true,
    description:
      'A wild Saccharomyces "bruxellensis" from Belgium — not a Brettanomyces at all, though it behaves like one with age. It forms a pellicle and, given time, develops big pineapple and stonefruit aromas over a dry finish with good mouthfeel. Works alone for a wild IPA or farmhouse beer, or blended into a sour or saison culture. Published as STA1 positive, so treat it as diastatic and keep it away from clean-beer equipment.',
    tags: ['wild', 'saccharomyces', 'diastatic', 'farmhouse', 'pellicle', 'pineapple'],
    url: 'https://www.jasperyeast.com/yeasts/47-sacc-brux',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy102',
    lab: 'Jasper Yeast',
    productCode: 'JY102',
    name: 'DC Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 85 },
    temperatureC: { min: 25, max: 33 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: true,
    description:
      'Recovered from a beer barrel in a Washington DC archaeological dig, this one throws a floral and fruity ester mix unlike any other American isolate. It needs genuine warmth to run, and like most saison strains it struggles in big worts, so keep the original gravity modest. Tests STA1 positive.',
    tags: ['saison', 'farmhouse', 'american', 'diastatic', 'floral', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/31-dc-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy104',
    lab: 'Jasper Yeast',
    productCode: 'JY104',
    name: 'Benedict Abbey',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Belgian abbey strain with clear spice on the nose and enough attenuation to keep lighter beers crisp. It shines in lower-strength blondes and in tripels, and is the house culture at a well-known Belgian-style brewery in Canada.',
    tags: ['belgian', 'abbey', 'blonde', 'tripel', 'phenolic'],
    url: 'https://www.jasperyeast.com/yeasts/29-benedict-abbey',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy112',
    lab: 'Jasper Yeast',
    productCode: 'JY112',
    name: 'Czech Lager II',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'Jasper’s second Czech pilsner strain, from the town where the style started. It leaves a clean, neutral, malt-accented beer with decent body and easy drinkability. Stays in suspension longer than JY001, so give it extra conditioning time.',
    tags: ['lager', 'czech', 'pilsner', 'clean', 'malty'],
    url: 'https://www.jasperyeast.com/yeasts/64-czech-lager-ii',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy127',
    lab: 'Jasper Yeast',
    productCode: 'JY127',
    name: 'Magic IPA',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 81 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Vermont-derived strain of the Conan type that has picked up firmer flocculation than its relatives. Fermented warm it turns fruity, and that fruit sits well alongside late hopping in pale ales, IPAs and imperial IPAs.',
    tags: ['american', 'hazy-ipa', 'conan', 'fruity', 'vermont', 'ipa'],
    url: 'https://www.jasperyeast.com/yeasts/5-magic-ipa',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy137',
    lab: 'Jasper Yeast',
    productCode: 'JY137',
    name: 'London 3',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 64, max: 74 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'The standard choice for New England IPA: it holds a stable haze, throws strong hop-leaning fruit esters and leaves a soft, full body because it does not touch maltotriose. Expect low attenuation, especially in strong worts. To dry it out, mash cooler, overpitch and finish warm. Also a good British cask strain.',
    tags: ['hazy-ipa', 'british', 'haze-stable', 'juicy', 'low-attenuation', 'cask'],
    url: 'https://www.jasperyeast.com/yeasts/13-london-3',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy153',
    lab: 'Jasper Yeast',
    productCode: 'JY153',
    name: 'Mexican Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 76 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A neutral workhorse aimed at light adjunct lagers and easy-drinking pale styles. Attenuation runs modest, so malt sits forward, yet the beer still reads crisp and refreshing. Suits pale through amber lagers equally well.',
    tags: ['lager', 'adjunct', 'neutral', 'crisp', 'sessionable'],
    url: 'https://www.jasperyeast.com/yeasts/20-mexican-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy157',
    lab: 'Jasper Yeast',
    productCode: 'JY157',
    name: 'Brett Brux Lambicus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 26, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The lambicus subspecies of Brettanomyces bruxellensis, and the loudest of Jasper’s Brett options: strong phenolics and heavy aromatics. Long ageing can push it towards an almost cherry-like tartness. It wants a warm fermentation. Jasper publishes attenuation only as 80+%, so the ceiling given here is representative.',
    tags: ['brettanomyces', 'lambicus', 'wild', 'phenolic', 'barrel-aged'],
    url: 'https://www.jasperyeast.com/yeasts/48-brett-brux-lambicus',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy158',
    lab: 'Jasper Yeast',
    productCode: 'JY158',
    name: 'Das Kolsch',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 15, max: 18 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A German ale strain rescued from a Washington DC brewery that makes soft, refreshing, faintly fruity beer. It throws sulfur during fermentation, especially when run cold, but that blows off with warmth or a short conditioning period. Good for Kölsch, Altbier and a middle-of-the-road American IPA, and it repitches well for another generation.',
    tags: ['kolsch', 'altbier', 'german', 'repitchable', 'sulfur'],
    url: 'https://www.jasperyeast.com/yeasts/42-das-kolsch',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy159',
    lab: 'Jasper Yeast',
    productCode: 'JY159',
    name: 'Castle',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 25, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Built for Belgian dark strong ales, with clove, pepper and a tart phenolic edge that deepens the warmer it runs. The published working range is narrow and hot, so plan for temperature control at the top end. It copes well with high-gravity worts and finishes dry enough to carry rich malts or fruit additions.',
    tags: ['belgian', 'dark-strong', 'phenolic', 'high-gravity', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/83-castle',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy160',
    lab: 'Jasper Yeast',
    productCode: 'JY160',
    name: 'Whitbread',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Built for deep attenuation and quick turnaround, which makes it a natural fit for bigger gravities. It drops out hard at almost any temperature, so it suits warm dry hopping, but chilling too aggressively mid-ferment can flocculate it out early and stall the beer. Rousing the cake will finish things off. A house strain at some very large IPA breweries.',
    tags: ['english', 'attenuative', 'ipa', 'fast-turnaround', 'dry-hop', 'high-floc'],
    url: 'https://www.jasperyeast.com/yeasts/16-whitbread',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy164',
    lab: 'Jasper Yeast',
    productCode: 'JY164',
    name: 'Mischief',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 20, max: 26 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Belgian golden strong specialist: clove-led phenolics with a touch of pepper, fruity esters and a little fermentation sulfur that blows off to leave a very dry, effervescent beer. Jasper lists it as diastatic, and it drops so slowly that it needs time or finings before packaging.',
    tags: ['belgian', 'golden-strong', 'diastatic', 'phenolic', 'dry-finish', 'low-flocculation'],
    url: 'https://www.jasperyeast.com/yeasts/84-mischief',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy169',
    lab: 'Jasper Yeast',
    productCode: 'JY169',
    name: 'Whippoorwhill Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 23, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'A Virginia farm isolate balancing rustic phenolics against bright esters, with earthy notes that sit well under a dry hop. It wants heat, running up to 35C, and like other saison strains it does poorly in high-gravity worts, so keep the original gravity down. Described as diastatic despite the modest published attenuation.',
    tags: ['saison', 'farmhouse', 'american', 'diastatic', 'earthy', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/78-whippoorwhill-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy173',
    lab: 'Jasper Yeast',
    productCode: 'JY173',
    name: 'Bluecollar',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 16, max: 24 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'Selected in-house at Jasper Yeast for dependability across a wide temperature band. It compacts hard once fermentation finishes, which makes it a practical pick for brewers with no filter or centrifuge and no time to babysit a tank.',
    tags: ['american', 'house-strain', 'robust', 'wide-temp-range', 'clean'],
    url: 'https://www.jasperyeast.com/yeasts/7-bluecollar',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy174',
    lab: 'Jasper Yeast',
    productCode: 'JY174',
    name: 'Andechser',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 71, max: 76 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'Said to trace back to a Bavarian monastery brewery, this bottom-fermenter gives a clean, malt-forward beer with very little ester or sulfur. The usable window is narrow at 10 to 12 C. It dislikes heavy spunding pressure, so apply it only on the way down in temperature.',
    tags: ['lager', 'german', 'malty', 'low-sulfur', 'monastery'],
    url: 'https://www.jasperyeast.com/yeasts/69-andechser',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy183',
    lab: 'Jasper Yeast',
    productCode: 'JY183',
    name: 'Tim T. (UK Ale XI)',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 67, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A cask-ale workhorse behind one of the best-known Yorkshire pale ales. It leaves nutty, fruity character over a chewy malt body and settles well enough that finings are rarely needed. Moderate attenuation keeps some sweetness in the glass.',
    tags: ['english', 'cask', 'yorkshire', 'malty', 'nutty', 'pale-ale'],
    url: 'https://www.jasperyeast.com/yeasts/17-tim-t-uk-ale-xi',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy188',
    lab: 'Jasper Yeast',
    productCode: 'JY188',
    name: 'Cadeaux',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Wallonian farmhouse strain giving peppery spice, zesty citrus, light tartness and a bubblegum note. Treat it as the higher-gravity sister to JY014: it takes bigger worts better, but fermentation is slow and needs patience before you call it finished. Diastatic, and Jasper publishes no flocculation figure for it beyond calling it moderate.',
    tags: ['saison', 'farmhouse', 'diastatic', 'peppery', 'slow-fermenting', 'high-gravity'],
    url: 'https://www.jasperyeast.com/yeasts/85-cadeaux',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy195',
    lab: 'Jasper Yeast',
    productCode: 'JY195',
    name: 'Monkfish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The strain behind a famously single-beer Trappist brewery, aimed at darker, bigger Belgian beers that still need to finish dry. Spice is mild and the ester bouquet is floral rather than loud. Its 14 percent alcohol tolerance is the highest in this range, which makes it the obvious pick for quadrupels.',
    tags: ['belgian', 'trappist', 'quadrupel', 'high-gravity', 'floral'],
    url: 'https://www.jasperyeast.com/yeasts/63-monkfish-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy196',
    lab: 'Jasper Yeast',
    productCode: 'JY196',
    name: 'Brett C',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 45, max: 65 },
    temperatureC: { min: 26, max: 32 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'Brettanomyces claussenii, the gentlest of the Brett species in common brewing use, leaning towards pineapple early and leather with age. It cannot carry a malt-based wort on its own — its fermentation capacity is too limited — so treat it as a secondary or blending culture behind a primary strain. Jasper does not publish an attenuation figure, so the range shown is representative only.',
    tags: ['brettanomyces', 'claussenii', 'wild', 'secondary', 'blending'],
    url: 'https://www.jasperyeast.com/yeasts/49-brett-c',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy199',
    lab: 'Jasper Yeast',
    productCode: 'JY199',
    name: 'Grodziskie',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A clean, neutral Polish ale strain for Grodziskie, the oak-smoked wheat beer sometimes called Polish champagne. It ferments out high and stays quiet on esters so the smoked malt and Polish hops carry the beer. Low flocculation leaves a delicate haze, which suits the style’s heavy carbonation and light body.',
    tags: ['grodziskie', 'polish', 'smoked', 'wheat', 'neutral', 'low-floc'],
    url: 'https://www.jasperyeast.com/yeasts/86-grodziskie',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy206',
    lab: 'Jasper Yeast',
    productCode: 'JY206',
    name: 'Ardennes',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A forgiving all-round Belgian strain from the Ardennes, spicy when kept cool and estery when run warm, so the same pitch can serve several styles depending on temperature and grist. It ferments without drama and drops clearer than most Belgian cultures, which makes it a good first Belgian yeast.',
    tags: ['belgian', 'all-purpose', 'spicy', 'reliable', 'estery'],
    url: 'https://www.jasperyeast.com/yeasts/30-ardennes',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy207',
    lab: 'Jasper Yeast',
    productCode: 'JY207',
    name: 'Belgian Wit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 18, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'The witbier culture traced to the Belgian brewery that revived the style, phenolic and spicy up front with an estery finish. Low flocculation keeps the traditional haze in the glass. The 8 percent alcohol tolerance keeps it in wit, Grand Cru wit and lighter specialty territory.',
    tags: ['belgian', 'witbier', 'phenolic', 'hazy', 'low-flocculation'],
    url: 'https://www.jasperyeast.com/yeasts/32-belgian-wit',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy208',
    lab: 'Jasper Yeast',
    productCode: 'JY208',
    name: 'Burton',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 20, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Burton-upon-Trent isolate giving apple and pear over a hard-candy ester note, drying out clean and malty. It prefers high-mineral liquor and handles stronger worts better than many British strains. Close kin to JY028, with softer structure and rather more ester expression.',
    tags: ['english', 'burton', 'estery', 'mineral', 'bitter', 'porter'],
    url: 'https://www.jasperyeast.com/yeasts/87-burton',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy214',
    lab: 'Jasper Yeast',
    productCode: 'JY214',
    name: 'Weizen I',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The archetypal Bavarian hefeweizen strain, traced back to Freising, and probably the most widely brewed weizen yeast anywhere. Banana ester and clove phenol sit in balance, and the brewer moves that balance with pitch rate, wort glucose and temperature. Very dusty, so it stays in suspension; it ferments hard and exothermically, so cool it properly or the pH will run away.',
    tags: ['hefeweizen', 'german', 'banana', 'clove', 'phenolic', 'dusty'],
    url: 'https://www.jasperyeast.com/yeasts/36-weizen-i',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy215',
    lab: 'Jasper Yeast',
    productCode: 'JY215',
    name: 'Chico Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'The reference American ale yeast, and the benchmark most other clean US strains are measured against. Attenuation is solid without running away, and the finish is smooth and neutral across pale ales, IPAs, ambers and stouts. Published alcohol tolerance is a range of 11 to 12 percent; the lower figure is recorded here.',
    tags: ['american', 'clean', 'classic', 'pale-ale', 'ipa', 'neutral'],
    url: 'https://www.jasperyeast.com/yeasts/11-chico-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy218',
    lab: 'Jasper Yeast',
    productCode: 'JY218',
    name: 'Augustiner',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 76 },
    temperatureC: { min: 11, max: 17 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A clean German strain favoured for hoppier Czech and German pilsners and for helles. Its lager character is more outspoken than most, and warmer fermentation pushes esters further forward. The range runs unusually warm for a lager at 11 to 17 C.',
    tags: ['lager', 'german', 'pilsner', 'helles', 'warm-tolerant'],
    url: 'https://www.jasperyeast.com/yeasts/22-augustiner',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy219',
    lab: 'Jasper Yeast',
    productCode: 'JY219',
    name: 'Bohemian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'Probably the most widely used lager strain anywhere, balancing gentle fruit against malt. It tolerates a broad temperature span and shines in kellerbier and other drinkable lagers. Pushed to around 16 to 21 C it also works for steam beer.',
    tags: ['lager', 'workhorse', 'kellerbier', 'balanced', 'steam-beer'],
    url: 'https://www.jasperyeast.com/yeasts/23-bohemian-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy221',
    lab: 'Jasper Yeast',
    productCode: 'JY221',
    name: 'Munich Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A sturdy Munich strain that attenuates well and finishes dry, making it a good fit for lean pilsners and crisp marzen or festbier. Sulfur stays low even at the cold end of its range. Also a strong pick for Italian pilsner and hop-forward lagers.',
    tags: ['lager', 'munich', 'dry', 'low-sulfur', 'festbier'],
    url: 'https://www.jasperyeast.com/yeasts/24-munich-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy228',
    lab: 'Jasper Yeast',
    productCode: 'JY228',
    name: 'Scottish Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 69, max: 76 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Famously reticent: pitched cool it contributes almost no ester character at all, which lets the malt do the talking. Attenuation stops short of dry, so a higher-than-usual final gravity is expected rather than a fault. Nudge the temperature up for a little more fruit. A sound house strain when you want the yeast to stay out of the way.',
    tags: ['scottish', 'malty', 'clean', 'low-ester', 'house-strain'],
    url: 'https://www.jasperyeast.com/yeasts/18-scottish-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy231',
    lab: 'Jasper Yeast',
    productCode: 'JY231',
    name: 'Frontiere Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 79 },
    temperatureC: { min: 21, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'Said to come from a well-known brewery in Blaugies, this one gives clove, earthy funk and a tart fruit edge over a dry peppery finish. Pitch it warm and let it climb, which is where the bubblegum and juicy character appears; it tolerates heat to 35C better than most. Diastatic, and unusually alcohol-tolerant for a saison strain at 12 percent.',
    tags: ['saison', 'farmhouse', 'diastatic', 'funky', 'peppery', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/77-frontiere-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy234',
    lab: 'Jasper Yeast',
    productCode: 'JY234',
    name: 'Common',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 68, max: 74 },
    temperatureC: { min: 10, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A dual-purpose strain that behaves like a normal lager yeast when kept cold but is built for California and Kentucky common when run warm, with a range stretching all the way to 21 C. Warm fermentation brings out the nutty, faintly woody character those steam styles are known for.',
    tags: ['lager', 'california-common', 'steam-beer', 'wide-range', 'nutty'],
    url: 'https://www.jasperyeast.com/yeasts/50-common',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy240',
    lab: 'Jasper Yeast',
    productCode: 'JY240',
    name: 'Sixtus',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 85 },
    temperatureC: { min: 21, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A widely used Belgian Trappist strain with a reputation for finishing dry and behaving predictably. Attenuation reaches 85 percent at the top of its range, so it suits blondes, dubbels and tripels, and it will even carry a witbier.',
    tags: ['belgian', 'trappist', 'abbey', 'dry-finish', 'reliable'],
    url: 'https://www.jasperyeast.com/yeasts/2-sixtus',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy243',
    lab: 'Jasper Yeast',
    productCode: 'JY243',
    name: 'Burlington Beer Factory',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'From a brewery known for hazy New England IPA, this strain deliberately stops short on attenuation to leave body and a rounded finish. Warm fermentation brings out fruit that layers into late hop character in pale ales, IPAs and doubles.',
    tags: ['hazy-ipa', 'american', 'low-attenuation', 'fruity', 'full-body'],
    url: 'https://www.jasperyeast.com/yeasts/6-burlington-beer-factory',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy244',
    lab: 'Jasper Yeast',
    productCode: 'JY244',
    name: 'Downeaster',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 81 },
    temperatureC: { min: 20, max: 24 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Vermont-lineage relative of JY127 that finishes drier, so a hazy IPA can carry more alcohol without turning sweet. Warm fermentation gives a distinctly peachy fruitiness that blends into late hop additions.',
    tags: ['hazy-ipa', 'american', 'vermont', 'peach', 'fruity', 'ipa'],
    url: 'https://www.jasperyeast.com/yeasts/12-downeaster',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy246',
    lab: 'Jasper Yeast',
    productCode: 'JY246',
    name: 'Hornindal',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 25, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'Jasper’s own Hornindal isolate, and a tropical one: mango, pineapple and guava are the usual descriptors. Despite decent flocculation it holds a stable haze in high-protein wheat and oat grists, which makes it a strong hazy IPA yeast. Dry-hopping during active fermentation pushes both the tropical character and the haze further.',
    tags: ['kveik', 'norwegian', 'hazy-ipa', 'tropical', 'fast-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/43-hornindal',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy247',
    lab: 'Jasper Yeast',
    productCode: 'JY247',
    name: 'Voss',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 77 },
    temperatureC: { min: 25, max: 37 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'Jasper’s own Voss isolate, the citrus end of the kveik spectrum and a natural partner for citrus-leaning hop blends. Orange, lemon and grapefruit come forward when it is deliberately underpitched at the top of its very wide temperature range. Like most kveik it ferments extremely fast and tolerates heat that would wreck a conventional ale strain.',
    tags: ['kveik', 'norwegian', 'citrus', 'fast-ferment', 'high-temperature'],
    url: 'https://www.jasperyeast.com/yeasts/44-voss',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy248',
    lab: 'Jasper Yeast',
    productCode: 'JY248',
    name: 'Malle Wit',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 17, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A louder witbier strain than the usual choices, reportedly out of West Flanders: orange zest and ripe banana over coriander and clove phenolics. It attenuates further than a typical wit culture, so expect a crisp, dry beer rather than a soft one, and low flocculation keeps the haze intact.',
    tags: ['belgian', 'witbier', 'estery', 'phenolic', 'hazy'],
    url: 'https://www.jasperyeast.com/yeasts/88-malle-wit',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy253',
    lab: 'Jasper Yeast',
    productCode: 'JY253',
    name: 'Arset Kveik',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 74, max: 82 },
    temperatureC: { min: 20, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'The clean kveik: it stays remarkably neutral across its whole temperature range, so it can turn around porters, stouts, English IPAs, ambers and light ales quickly without an ester signature. Push it warm and soft pear and apple notes appear, most obvious in lightly hopped beers. It dislikes big wort and should not be asked to work above about 18°P.',
    tags: ['kveik', 'norwegian', 'clean', 'neutral', 'fast-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/41-arset-kveik',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy254',
    lab: 'Jasper Yeast',
    productCode: 'JY254',
    name: 'Ginjo',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 16, max: 20 },
    flocculation: 'low',
    alcoholTolerancePercent: 16,
    sta1: null,
    description:
      'A sake strain isolated from a well-known non-foaming yeast, giving fruity, bubblegum-tinged aromatics. It is known for strong but slow fermentations and is the usual choice for ginjo-style sake with a fruity, floral nose. Very high alcohol tolerance for a Saccharomyces cerevisiae.',
    tags: ['sake', 'japanese', 'ginjo', 'fruity', 'high-alcohol'],
    url: 'https://www.jasperyeast.com/yeasts/59-ginjo',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy255',
    lab: 'Jasper Yeast',
    productCode: 'JY255',
    name: 'Ginjo-ka',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 12, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A sake strain that keeps fermenting strongly at genuinely cold temperatures, which makes it easy to run slow and cool. It produces less acidity than most sake yeasts and leaves pleasant citrus notes behind. The widest temperature range of Jasper’s sake set.',
    tags: ['sake', 'japanese', 'ginjo', 'citrus', 'cold-tolerant'],
    url: 'https://www.jasperyeast.com/yeasts/60-ginjo-ka',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy256',
    lab: 'Jasper Yeast',
    productCode: 'JY256',
    name: 'Apple',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 90 },
    temperatureC: { min: 20, max: 25 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A sake strain that leaves a clear green-apple character, distinct enough to be the point of the beverage rather than an off note. It prefers the warm end of the sake range and performs better there than cool. Attenuates hard, so expect a dry finish.',
    tags: ['sake', 'japanese', 'green-apple', 'fruity', 'warm-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/61-apple',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy257',
    lab: 'Jasper Yeast',
    productCode: 'JY257',
    name: 'Daiginjo',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 15, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'A sake strain suited to cold, slow fermentation, which is where its fruity aroma develops best. It is the natural pick for ginjo and daiginjo work. Very dusty, so it stays in suspension and needs time or filtration to clear.',
    tags: ['sake', 'japanese', 'daiginjo', 'fruity', 'cold-ferment', 'dusty'],
    url: 'https://www.jasperyeast.com/yeasts/62-daiginjo',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy258',
    lab: 'Jasper Yeast',
    productCode: 'JY258',
    name: 'High Pressure Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 12, max: 21 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'Built for turning around a lager in about a week: ferment at roughly 17 to 20 C under 6 to 15 PSI of spunding pressure, then cold condition for five to seven days. The head pressure suppresses esters, so the beer stays clean despite ale-range temperatures. Frees up tank time on a fast schedule.',
    tags: ['lager', 'pressure-fermentation', 'fast', 'spunding', 'clean'],
    url: 'https://www.jasperyeast.com/yeasts/25-high-pressure-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy265',
    lab: 'Jasper Yeast',
    productCode: 'JY265',
    name: 'Notre-Dame',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 23, max: 29 },
    flocculation: 'medium',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Trappist-style strain for dark fruit: plum, raisin and cherry with muted clove and black pepper. The published range starts hot at 23C, and the restrained attenuation leaves a smooth, slightly sweet finish that suits dubbels, quadrupels and Belgian strong ales. Fermented at the cool end it turns surprisingly clean, with very little Belgian character.',
    tags: ['belgian', 'trappist', 'dubbel', 'dark-fruit', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/89-notre-dame',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy267',
    lab: 'Jasper Yeast',
    productCode: 'JY267',
    name: 'Swiss',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 79 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A precise, neutral lager strain thought to come from a well-known Swiss brewery. It attenuates high and stays out of the way, which suits Swiss and Italian pilsners and other delicate hop-driven beers. Fermented cold it adds faint herbal and floral notes, and it drops clear readily.',
    tags: ['lager', 'swiss', 'italian-pilsner', 'neutral', 'hop-forward'],
    url: 'https://www.jasperyeast.com/yeasts/70-swiss',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy268',
    lab: 'Jasper Yeast',
    productCode: 'JY268',
    name: 'Franconian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 78 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A balanced, clean Franconian strain for bohemian pilsner, classic marzen or a seasonal doppelbock. It resembles JY219 but attenuates a little further and throws very little sulfur. Recovered from old stock in a German lab after decades of neglect.',
    tags: ['lager', 'franconian', 'pilsner', 'low-sulfur', 'doppelbock'],
    url: 'https://www.jasperyeast.com/yeasts/21-franconian-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy272',
    lab: 'Jasper Yeast',
    productCode: 'JY272',
    name: 'Bavarian Cloister',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Saccharomyces pastorianus strain from a Bavarian monastery that ferments cleanly and leaves a bready, creamy impression. Esters and sulfur stay low as long as you hold the narrow 10 to 12 C window. Solid attenuation and a higher than usual alcohol tolerance cover everything from helles to bock.',
    tags: ['lager', 'bavarian', 'bready', 'helles', 'bock'],
    url: 'https://www.jasperyeast.com/yeasts/90-bavarian-cloister',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy273',
    lab: 'Jasper Yeast',
    productCode: 'JY273',
    name: 'Aying',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 10, max: 12 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A malt-first Bavarian strain suited to heavier and darker lagers. Esters and sulfur are minimal so the grain bill carries the beer. It works in a tight 10 to 12 C band and repays a proper diacetyl rest before lagering.',
    tags: ['lager', 'german', 'malty', 'dark-lager', 'diacetyl-rest'],
    url: 'https://www.jasperyeast.com/yeasts/91-aying',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy274',
    lab: 'Jasper Yeast',
    productCode: 'JY274',
    name: 'Warpfeed',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 100, max: 100 },
    temperatureC: { min: 23, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'Jasper’s dedicated hard seltzer strain, selected to take a sugar wash to full attenuation with no off aromas. It runs hot at 23 to 29 C, tolerates 14 percent alcohol and settles quickly enough to skip filtration or finings. Intended for use with Jasper’s Warp-Feed nutrient, added once in the boil, which also limits the yellow tint common with other nutrients.',
    tags: ['seltzer', 'sugar-wash', 'neutral', 'high-abv', 'fast-fermenting'],
    url: 'https://www.jasperyeast.com/yeasts/8-warpfeed',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy278',
    lab: 'Jasper Yeast',
    productCode: 'JY278',
    name: 'Philly Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A pre-Prohibition American lager strain with a malty, faintly bready backbone and light apple and pear esters. It is forgiving and handles pressure fermentation well. Good for revived classic American lagers, cream ales and steam beers, and it clears without much coaxing.',
    tags: ['lager', 'american', 'pre-prohibition', 'cream-ale', 'estery'],
    url: 'https://www.jasperyeast.com/yeasts/71-philly-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy280',
    lab: 'Jasper Yeast',
    productCode: 'JY280',
    name: 'Geoff-e',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 79 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A German weizen strain in the same family as JY214 but quieter on isoamyl acetate, so the banana stays in check even at the warm end of its range. Clove and banana land in balance around the high teens Celsius, giving a lively but composed hefeweizen fermentation. Stocked originally for a local brewer with decades of pub-brewing behind him.',
    tags: ['hefeweizen', 'german', 'clove', 'banana', 'balanced'],
    url: 'https://www.jasperyeast.com/yeasts/37-geoff-e',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy281',
    lab: 'Jasper Yeast',
    productCode: 'JY281',
    name: 'Ebbegarden',
    form: 'liquid',
    type: 'kveik',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 35 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Norwegian farmhouse isolate from Stordal that ferments reliably across an unusually wide range. Run it hot and it gives mango, guava and citrus peel; run it cool and it turns restrained. Unlike most kveik it sharpens rather than softens hop bitterness, which suits a hazy IPA fermented fast.',
    tags: ['kveik', 'norwegian', 'hazy-ipa', 'tropical', 'fast-fermentation', 'warm-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/92-ebbegarden',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy284',
    lab: 'Jasper Yeast',
    productCode: 'JY284',
    name: 'Belgian Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 15 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'Taken from a traditional Belgian lager brewery, this strain makes clean, drinkable pale lagers with a hint of malt sweetness. It suits golden pilsners and light blonde lagers where restraint is the point. Attenuates well and settles out at a moderate rate.',
    tags: ['lager', 'belgian', 'pilsner', 'clean', 'blonde'],
    url: 'https://www.jasperyeast.com/yeasts/72-belgian-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy285',
    lab: 'Jasper Yeast',
    productCode: 'JY285',
    name: 'Smoked Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 60, max: 72 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Bamberg strain from a brewery known for smoked malt beers. It deliberately underattenuates compared with other lager yeasts, with a published floor as low as 60 percent, leaving body and residual sweetness for the rauchmalt character to sit on. Choose it when you want the smoke supported rather than stripped out.',
    tags: ['lager', 'rauchbier', 'bamberg', 'low-attenuation', 'full-bodied'],
    url: 'https://www.jasperyeast.com/yeasts/73-smoked-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy287',
    lab: 'Jasper Yeast',
    productCode: 'JY287',
    name: 'American Wheat',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A Pacific Northwest strain for American wheat beer, clean of the clove and banana a hefeweizen yeast would bring. It finishes crisp with a hint of tartness and light citrus, leaving malt and hops in front, and its low flocculation helps keep a wheat beer hazy in the glass.',
    tags: ['american', 'wheat', 'clean', 'citrus', 'pale-ale', 'hazy'],
    url: 'https://www.jasperyeast.com/yeasts/93-american-wheat',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy293',
    lab: 'Jasper Yeast',
    productCode: 'JY293',
    name: 'Milwaukee Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 15, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A heritage Milwaukee ale strain that ferments clean and leans slightly malty, with only mild fruit esters. Moderate attenuation and a wide working temperature make it a steady all-round choice for classic American ales.',
    tags: ['american', 'heritage', 'clean', 'malt-forward', 'balanced'],
    url: 'https://www.jasperyeast.com/yeasts/95-milwaukee-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy302',
    lab: 'Jasper Yeast',
    productCode: 'JY302',
    name: 'Gravity Bender',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 19, max: 23 },
    flocculation: 'medium',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'A neutral strain with exceptional alcohol tolerance, able to start above 30 degrees Plato and still finish near 3.5 to 4 Plato. In ordinary worts it behaves like any clean ale yeast and does not over-attenuate, so it is aimed at imperial stouts, triple IPAs and barleywines meant to end dry.',
    tags: ['american', 'high-gravity', 'alcohol-tolerant', 'barleywine', 'imperial-stout', 'clean'],
    url: 'https://www.jasperyeast.com/yeasts/58-gravity-bender',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy303',
    lab: 'Jasper Yeast',
    productCode: 'JY303',
    name: 'Particle Wave',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 65, max: 72 },
    temperatureC: { min: 23, max: 30 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A non-GMO thiol-releasing strain: it cleaves bound precursors out of malt and hops during fermentation, so the beer gains free citrus, pomelo and pineapple aroma that no hop addition alone would deliver. Because it is not engineered, that release comes with far less sulfur than bacterial-gene strains. Ferment warm and let it free-rise to about 30C; attenuation is modest, so keep worts above roughly 18 Plato highly fermentable.',
    tags: ['thiol', 'hazy-ipa', 'non-gmo', 'tropical', 'biotransformation', 'warm-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/57-particle-wave',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy305',
    lab: 'Jasper Yeast',
    productCode: 'JY305',
    name: 'Berlin Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 75, max: 82 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A dependable malt-focused German strain that adds a touch of pear-like fruit without softening the crisp lager finish. It fits pilsner, bock, oktoberfest and darker lagers alike. Attenuation reaches into the low eighties with a suitable mash, and it drops fast and clear.',
    tags: ['lager', 'german', 'malty', 'bock', 'high-attenuation'],
    url: 'https://www.jasperyeast.com/yeasts/74-berlin-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy309',
    lab: 'Jasper Yeast',
    productCode: 'JY309',
    name: 'Wirral Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 6,
    sta1: null,
    description:
      'From a well-known cask brewery on the Wirral, this one ferments cleanly and pushes citrus and floral hop character forward, with biscuit and light orange-zest fruit underneath. Best in session bitters, golden ales and pales. It favours open fermentation, crops and repitches easily, and dislikes strong worts.',
    tags: ['english', 'cask', 'hop-forward', 'golden-ale', 'session', 'open-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/96-wirral-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy311',
    lab: 'Jasper Yeast',
    productCode: 'JY311',
    name: 'River Thames',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A London brewery strain with soft apricot and pear esters set against gentle malt sweetness and a faintly tart close. Moderate attenuation and settling give a clear, balanced pint, and its alcohol tolerance opens up bigger beers. Keep the mineral content middling rather than extreme. Suits bitters, ESBs and porters.',
    tags: ['english', 'london', 'bitter', 'esb', 'porter', 'fruity'],
    url: 'https://www.jasperyeast.com/yeasts/97-river-thames',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy313',
    lab: 'Jasper Yeast',
    productCode: 'JY313',
    name: 'Autolactic',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 72, max: 80 },
    temperatureC: { min: 25, max: 29 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A Jasper isolate that acidifies and ferments wort in a single pitch, reaching pH 3.0–3.5 depending on conditions, with a fruity character alongside the acidity. Unlike a Lactobacillus it can carry the fermentation without a co-pitch, though it is slower than a conventional ale yeast. Oxygenate well and keep it warm start to finish — below about 27°C it can stall.',
    tags: ['quick-sour', 'souring', 'wild', 'single-pitch', 'fruity'],
    url: 'https://www.jasperyeast.com/yeasts/65-autolactic',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy315',
    lab: 'Jasper Yeast',
    productCode: 'JY315',
    name: 'Limerick',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 20, max: 29 },
    flocculation: 'low',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'An American saison strain with a vinous aroma and lemon and lime peel flavor over a crisp finish, and a good base for fruit additions. It ferments dependably but drops very slowly, leaving a natural haze unless you use finings, and it wants heavy oxygenation at cast-out. Described as strongly diastatic, so expect it to keep attenuating in package.',
    tags: ['saison', 'farmhouse', 'american', 'diastatic', 'citrus', 'hazy', 'low-flocculation'],
    url: 'https://www.jasperyeast.com/yeasts/98-limerick',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy316',
    lab: 'Jasper Yeast',
    productCode: 'JY316',
    name: 'Newcastle',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A north-east English strain for traditional bitters and session beers, giving light pear and floral notes with very little sulfur. Fermentation is unhurried, often a week or two, and it needs soft, low-mineral wort to perform. Poor settling leaves a soft haze that suits ordinary bitters, best bitters and milds. Keep the gravity modest.',
    tags: ['english', 'newcastle', 'bitter', 'mild', 'soft-water', 'hazy'],
    url: 'https://www.jasperyeast.com/yeasts/99-newcastle',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy319',
    lab: 'Jasper Yeast',
    productCode: 'JY319',
    name: 'Dartmoor',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A lively West Country strain for brewers who want the yeast to be part of the flavor. It ferments fast and throws pronounced banana and tropical esters plus a sulfur note that conditions away, with a hint of spice from mild phenolics. Poor settling leaves the beer only moderately bright. Give it soft water and a traditional gravity.',
    tags: ['english', 'west-country', 'estery', 'bitter', 'phenolic', 'soft-water'],
    url: 'https://www.jasperyeast.com/yeasts/100-dartmoor',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy320',
    lab: 'Jasper Yeast',
    productCode: 'JY320',
    name: 'South London',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A polished southern English heritage strain aimed at best bitters and strong ales. It ferments slowly, typically a week or two, which builds malt depth, and leaves mild stone-fruit esters with a faint herbal edge and almost no sulfur. It needs high-mineral wort to finish, and carries alcohol better than most heritage English isolates.',
    tags: ['english', 'london', 'heritage', 'best-bitter', 'strong-ale', 'slow-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/101-south-london',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy322',
    lab: 'Jasper Yeast',
    productCode: 'JY322',
    name: 'North Yorkshire',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 16, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'A heritage Yorkshire strain giving moderate esters and a nutty edge, with a light sulfur note that fades in conditioning. It is lightly POF positive, so a whisper of spice can show up in the finish. Krausen is enormous, so plan for headspace, rousing or antifoam. It needs high-mineral wort to finish and does not like strong worts.',
    tags: ['english', 'yorkshire', 'heritage', 'bitter', 'nutty', 'big-krausen'],
    url: 'https://www.jasperyeast.com/yeasts/102-north-yorkshire',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy323',
    lab: 'Jasper Yeast',
    productCode: 'JY323',
    name: 'Leicester',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A heritage Midlands strain for malt-driven traditional bitters. Esters stay low, offering little more than biscuit and a hint of fruit, and sulfur is minimal for a crisp finish. It wants high-mineral wort, ferments slowly over a week or two, and prefers to be kept at the warm end of its range.',
    tags: ['english', 'heritage', 'bitter', 'malty', 'clean', 'slow-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/103-leicester',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy324',
    lab: 'Jasper Yeast',
    productCode: 'JY324',
    name: 'Canterbury',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 83 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A single-strain Kentish heritage yeast behind the classic Southern English bitter profile. It attenuates deeply and gives orchard-fruit esters with a peppery POF-positive lift and a low sulfur note that blows off in conditioning. Jasper describe its settling as very low and dusty, so expect a rustic haze that suits cask and unfined bottle-conditioned beer.',
    tags: ['english', 'kent', 'heritage', 'bitter', 'cask', 'phenolic', 'hazy'],
    url: 'https://www.jasperyeast.com/yeasts/104-canterbury',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy325',
    lab: 'Jasper Yeast',
    productCode: 'JY325',
    name: 'Lancaster',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'A bold Lancashire heritage strain that ferments quickly and leaves plenty of ester, with plum, dark cherry and a toffee edge over a low sulfur background. It settles readily into a firm cake for a bright beer. High-mineral wort brings out the malt depth. Best in milds, ordinary and best bitters at traditional gravities.',
    tags: ['english', 'lancashire', 'heritage', 'mild', 'bitter', 'estery'],
    url: 'https://www.jasperyeast.com/yeasts/105-lancaster',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy328',
    lab: 'Jasper Yeast',
    productCode: 'JY328',
    name: 'Eifel Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 77 },
    temperatureC: { min: 8, max: 12 },
    flocculation: 'low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A cold-running German strain for pilsner, American lager and vienna lager, with a short lag time even at the low end of its range. Esters are minimal and the finish is dry with a faint grainy note. Low flocculation leaves haze that needs a diacetyl rest and extended lagering to clear, and its 7 percent alcohol tolerance rules out strong or high-gravity worts.',
    tags: ['lager', 'german', 'pilsner', 'cold-ferment', 'low-abv'],
    url: 'https://www.jasperyeast.com/yeasts/106-eifel-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy329',
    lab: 'Jasper Yeast',
    productCode: 'JY329',
    name: 'Kings Ale',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'low',
    alcoholTolerancePercent: 6,
    sta1: null,
    description:
      'A genuine heritage English strain that Jasper recovered from a bottle of 1903 Bass Kings Ale. It ferments dependably and is mildly POF positive, so a light phenolic spice comes through. This is a recent isolate and the published characterization is still thin, so treat the numbers as early figures.',
    tags: ['english', 'heritage', 'historic', 'phenolic', 'bass', 'new-isolate'],
    url: 'https://www.jasperyeast.com/yeasts/107-kings-ale',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy330',
    lab: 'Jasper Yeast',
    productCode: 'JY330',
    name: 'Dutch Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 8, max: 13 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A historic Dutch pale lager strain that finishes smooth with a perception of malt sweetness. Attenuation spans a wide 70 to 80 percent depending on wort and mash. It is a hardy strain that copes with fermentation stress and settles out at a moderate rate.',
    tags: ['lager', 'dutch', 'smooth', 'malty', 'hardy'],
    url: 'https://www.jasperyeast.com/yeasts/67-dutch-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy334',
    lab: 'Jasper Yeast',
    productCode: 'JY334',
    name: 'Safety Saison',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 85 },
    temperatureC: { min: 24, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'Jasper markets this as a saison strain without the diastatic activity that keeps some brewers away from the style, while still reaching 85 percent attenuation. It needs a hot fermentation, from 24C up to 32C, and rewards the top of that range with fuller spice and ester character. It also flocculates better than the classic saison cultures.',
    tags: ['saison', 'farmhouse', 'non-diastatic', 'high-attenuation', 'warm-fermentation'],
    url: 'https://www.jasperyeast.com/yeasts/66-safety-saison',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy335',
    lab: 'Jasper Yeast',
    productCode: 'JY335',
    name: 'Stange',
    form: 'liquid',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 15, max: 17 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A Kölsch-type Saccharomyces cerevisiae that stays out of the way of the hops, adding only faint citrus and floral esters. Cold fermentation keeps it neutral and crisp; run it warmer and it turns noticeably more fruit-forward. It can be slow to start but then holds a steady pace and drops reasonably bright.',
    tags: ['kolsch', 'german', 'clean', 'neutral', 'hop-forward'],
    url: 'https://www.jasperyeast.com/yeasts/108-stange',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy336',
    lab: 'Jasper Yeast',
    productCode: 'JY336',
    name: 'Sheffield Bitter',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 75 },
    temperatureC: { min: 17, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 7,
    sta1: null,
    description:
      'A heritage strain from a well-known South Yorkshire brewery, aimed squarely at sessionable cask bitter. The ferment is clean and malt-forward, leaving gentle biscuit and soft apple with a slightly nutty finish that flatters moderate bitterness. It wants high-mineral wort and a sensible gravity.',
    tags: ['english', 'yorkshire', 'heritage', 'cask', 'bitter', 'session', 'malty'],
    url: 'https://www.jasperyeast.com/yeasts/109-sheffield-bitter',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy337',
    lab: 'Jasper Yeast',
    productCode: 'JY337',
    name: 'Urs Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 72, max: 78 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'high',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A strongly flocculating German microbrewery strain that ferments slowly but steadily, attenuates well and drops bright. It leaves a touch of sweetness from yeast character alone, which suits hop-flavor-forward and light Asian-style lagers. It also shows mild thiol-releasing activity where precursors are present, though the effect is subtle and the strain is not genetically modified.',
    tags: ['lager', 'german', 'high-floc', 'thiolized', 'slow-ferment'],
    url: 'https://www.jasperyeast.com/yeasts/75-urs-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy338',
    lab: 'Jasper Yeast',
    productCode: 'JY338',
    name: 'Brewpub',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 70, max: 80 },
    temperatureC: { min: 16, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'The all-purpose brewpub strain of the 1990s and 2000s, clean enough for hoppy beer and full enough for stouts. It builds mouthfeel and pushes malt and caramel depth without leaving sweetness, and its low flocculation means slight haze unless you fine or cold-condition. It can start slowly on first generation, so give it extra oxygen.',
    tags: ['american', 'versatile', 'full-body', 'amber', 'porter', 'hazy'],
    url: 'https://www.jasperyeast.com/yeasts/110-brewpub',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy345',
    lab: 'Jasper Yeast',
    productCode: 'JY345',
    name: 'Minnesota Lager',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 8, max: 14 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'An American lager strain of Tuborg lineage, more expressive than most of its peers. Its ester character stays in balance with the malt, which makes it a good match for pre-Prohibition style lagers, light lagers and cream ale.',
    tags: ['lager', 'american', 'tuborg', 'estery', 'cream-ale'],
    url: 'https://www.jasperyeast.com/yeasts/76-minnesota-lager',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy347',
    lab: 'Jasper Yeast',
    productCode: 'JY347',
    name: 'Ringwood',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 68, max: 72 },
    temperatureC: { min: 20, max: 23 },
    flocculation: 'high',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A vigorous top-cropping English strain for malt-forward beer, layering distinct fruit esters over a substantial malt backbone. It settles hard enough to drop bright without filtration, but it demands attention: start-up is slow, so oxygenate again on day two, keep it off the cold end, let it free rise past halfway, and allow a long diacetyl rest.',
    tags: ['english', 'heritage', 'top-cropping', 'malty', 'diacetyl-rest', 'high-floc'],
    url: 'https://www.jasperyeast.com/yeasts/111-ringwood',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy349',
    lab: 'Jasper Yeast',
    productCode: 'JY349',
    name: 'Fruit Ninja',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 23 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A natural, non-GMO strain with real beta-lyase activity, meaning it frees bound thiols from malt and hops during fermentation and turns them into loud passionfruit and grapefruit aroma. Unlike most thiol strains it also attenuates properly, so a hazy IPA finishes where you expect it to.',
    tags: ['thiol', 'hazy-ipa', 'non-gmo', 'passionfruit', 'grapefruit', 'fruit-forward'],
    url: 'https://www.jasperyeast.com/yeasts/113-fruit-ninja',
    provenance: 'spec-sheet',
  },
  {
    id: 'jasper-jy-lplant',
    lab: 'Jasper Yeast',
    productCode: 'JY-LPLANT',
    name: 'Lactobacillus plantarum',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 29, max: 37 },
    flocculation: 'low',
    alcoholTolerancePercent: 5,
    sta1: null,
    description:
      'A souring bacterium, not a fermenting yeast: it drops wort pH by producing lactic acid and cannot make beer on its own, so it needs a primary yeast pitched after or alongside it. Homofermentative and hop-sensitive — it struggles above about 10 IBU — which makes it a comparatively safe organism to keep in a brewery. Jasper publishes 0% attenuation, so the band shown here is a placeholder rather than a measured figure.',
    tags: ['lactobacillus', 'souring', 'kettle-sour', 'bacteria', 'hop-sensitive'],
    url: 'https://www.jasperyeast.com/yeasts/52-lactobacillus-plantarum',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy-lbrev',
    lab: 'Jasper Yeast',
    productCode: 'JY-LBREV',
    name: 'Lactobacillus brevis',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 26, max: 35 },
    flocculation: 'low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A souring bacterium for acidifying wort, not a primary fermentation on its own — it always needs a yeast to finish the beer. Less hop-sensitive than L. plantarum but heterofermentative, so expect CO2 and a little ethanol or acetate alongside the lactic acid. It works cooler than plantarum, and pitching the two together broadens the temperature window for reliable souring. Jasper publishes 0% attenuation, so the band here is a placeholder.',
    tags: ['lactobacillus', 'souring', 'kettle-sour', 'bacteria', 'heterofermentative'],
    url: 'https://www.jasperyeast.com/yeasts/53-lactobacillus-brevis',
    provenance: 'representative',
  },
  {
    id: 'jasper-jy-lacid',
    lab: 'Jasper Yeast',
    productCode: 'JY-LACID',
    name: 'Lactobacillus acidophilus',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 35, max: 40 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The hottest-running of Jasper’s four Lactobacillus species, homofermentative and strongly probiotic, and a producer of natural inhibitors that suppress spoilage organisms during a sour. Like any Lactobacillus it acidifies wort and does not ferment it, so a yeast pitch is still required. Jasper publishes no attenuation figure at all, so the band shown is a placeholder rather than data.',
    tags: ['lactobacillus', 'souring', 'kettle-sour', 'bacteria', 'thermophilic'],
    url: 'https://www.jasperyeast.com/yeasts/54-lactobacillus-acidophilus',
    provenance: 'representative',
  },
  {
    id: 'jasper-na304',
    lab: 'Jasper Yeast',
    productCode: 'NA304',
    name: 'Dez',
    form: 'liquid',
    type: 'wild',
    attenuationPercent: { min: 30, max: 35 },
    temperatureC: { min: 21, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 4,
    sta1: null,
    description:
      'A Saccharomycodes ludwigii for non-alcoholic and low-alcohol beer: it cannot use maltose or longer sugars, so it stops early by design. Jasper publishes about 20% apparent attenuation, which falls below the range this field allows, so the figures here are representative. A low-ABV beer is far more vulnerable to spoilage than a normal one, and post-fermentation pH correction and pasteurization may be needed for a shelf-stable product.',
    tags: ['non-alcoholic', 'low-alcohol', 'saccharomycodes', 'maltose-negative', 'specialty'],
    url: 'https://www.jasperyeast.com/yeasts/55-dez',
    provenance: 'representative',
  },
];
